import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars } from "@fortawesome/free-solid-svg-icons";
import { ModalContext } from "../contexts/ModalContext";
import SectionRowHistoryModal from "./SectionRowHistoryModal";
import React, { useRef } from "react";
import RemirrorEditor from "../components/RemirrorEditor";
import RemirrorSectionRowToolbarPlugin from "../components/RemirrorSectionRowToolbarPlugin";

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import ConfirmModal from "./utils/ConfirmModal";
import JustificationNoteModal from "./utils/JustificationNoteModal";
import {
  DeletePCRSEntry,
  EditPCRSEntryText,
  HardDeletePCRSEntry,
  ResetPCRSEntry,
  RestorePCRSEntry,
} from "../paths/pcrs";
import { ToastMessageContext } from "../contexts/ToastMessageContext";
import SafeHtml from "../components/SafeHtml";

export default function SectionRow({
  assessmentID,
  id,
  initialEditVal,
  text,
  setSectionDataFct,
  deleted,
  isAmended = false,
  historyCount = 0,
}) {
  let { openModal } = React.useContext(ModalContext);
  let { toastMessage } = React.useContext(ToastMessageContext);
  const [editFlag, setEditFlag] = React.useState(initialEditVal);
  const [html, setHtml] = React.useState(text);
  let lastSavedText = useRef(text);

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });
  const style = {
    transition,
    transform: CSS.Translate.toString(transform),
  };

  function openHistoryModal(rowID) {
    openModal(
      <SectionRowHistoryModal assessmentID={assessmentID} entryID={rowID} />,
      "xl"
    );
  }

  function openConfirmResetTextModal(rowID) {
    let title = "Réinitialiser le texte";
    let confirmMessage =
      "Êtes-vous sûr de vouloir réinitialiser le texte de cette phrase à sa valeur par défaut?";

    const resetEntry = async (params) => {
      let results = await ResetPCRSEntry(assessmentID, rowID);
      if (results.success) {
        setHtml(results.entryNewText);
      } else {
        toastMessage(
          "Une erreur est survenue. Le texte original de la phrase n'a pas été restauré",
          "danger"
        );
      }
      return results.success;
    };
    openModal(
      <ConfirmModal
        title={title}
        message={confirmMessage}
        handleAccept={resetEntry}
      />,
      "md"
    );
  }

  function openJustificationModalForDelete(rowID) {
    openModal(
      <JustificationNoteModal
        modalTitle="Supprimer la phrase"
        message="Vous pouvez ajouter une note justificative pour cette modification (visible dans l'historique de l'amendement)."
        submitBtnText="Supprimer"
        placeholder="Ex. : Suppression du titre « fumeur » à la demande du patient."
        onConfirm={async (justificationNote) => {
          const result = await DeletePCRSEntry(
            assessmentID,
            rowID,
            justificationNote
          );
          if (result.success) {
            setSectionDataFct(result.sectionNewData);
          } else {
            toastMessage(
              "Une erreur est survenue. La phrase n'a pas été supprimée",
              "danger"
            );
          }
          return result.success;
        }}
      />,
      "md"
    );
  }

  async function softDeleteEntry(rowID) {
    if (isAmended) {
      openJustificationModalForDelete(rowID);
      return;
    }
    const result = await DeletePCRSEntry(assessmentID, rowID);
    if (result.success) {
      setSectionDataFct(result.sectionNewData);
    } else {
      toastMessage(
        "Une erreur est survenue. La phrase n'a pas été supprimée",
        "danger"
      );
    }
  }

  async function restoreEntry(rowID) {
    let results = await RestorePCRSEntry(assessmentID, rowID);
    if (results.success) {
      setSectionDataFct(results.sectionNewData);
    } else {
      toastMessage(
        "Une erreur est survenue. La phrase n'a pas été restaurée",
        "danger"
      );
    }
  }

  function openConfirmHardDeleteModal(rowID) {
    let title = "Supprimer définitivement la phrase";
    let confirmMessage =
      "Êtes-vous sûr de vouloir supprimer définitivement cette phrase?  Elle ne pourra pas être restaurée";

    const hardDeleteEntry = async (params) => {
      let results = await HardDeletePCRSEntry(assessmentID, rowID);
      if (results.success) {
        setSectionDataFct(results.sectionNewData);
      } else {
        toastMessage(
          "Une erreur est survenue. La phrase n'a pas été définitivement supprimée",
          "danger"
        );
      }
      return results.success;
    };

    openModal(
      <ConfirmModal
        title={title}
        message={confirmMessage}
        handleAccept={hardDeleteEntry}
      />,
      "md"
    );
  }

  function openJustificationModalForSave(assessmentID, entryID) {
    openModal(
      <JustificationNoteModal
        modalTitle="Enregistrer la modification"
        message="Vous pouvez ajouter une note justificative pour cette modification (visible dans l'historique de l'amendement)."
        submitBtnText="Enregistrer"
        placeholder="Ex. : Suppression du titre « fumeur » à la demande du patient."
        onConfirm={async (justificationNote) => {
          const result = await EditPCRSEntryText(
            assessmentID,
            entryID,
            html,
            justificationNote
          );
          if (result.success) {
            setHtml(result.newText);
            setEditFlag(false);
            updateEntryHistoryCount(entryID);
          } else {
            toastMessage(
              result.error,
              "danger",
              "Une erreur est survenue. Le texte n'a pas été sauvegardé"
            );
          }
          return result.success;
        }}
      />,
      "md"
    );
  }

  function updateEntryHistoryCount(entryID, increment = 1) {
    setSectionDataFct((prev) =>
      prev.map((entry) =>
        entry.id === entryID
          ? {
              ...entry,
              history_count: (entry.history_count ?? 0) + increment,
            }
          : entry
      )
    );
  }

  async function saveEntryNewText(assessmentID, entryID) {
    if (isAmended) {
      openJustificationModalForSave(assessmentID, entryID);
      return;
    }
    const result = await EditPCRSEntryText(assessmentID, entryID, html);
    if (result.success) {
      setHtml(result.newText);
      setEditFlag(false);
      updateEntryHistoryCount(entryID);
    } else {
      toastMessage(
        result.error,
        "danger",
        "Une erreur est survenue. Le texte n'a pas été sauvegardé"
      );
    }
  }

  const startTextEdit = () => {
    lastSavedText.current = html;
    setEditFlag(true);
  };

  const cancelTextEdit = () => {
    setHtml(lastSavedText.current);
    setEditFlag(false);
  };

  const toolbarPluginProps = {
    editMode: editFlag,
    deleted,
    historyCount,
    onSave: () => saveEntryNewText(assessmentID, id),
    onCancel: cancelTextEdit,
    onEdit: startTextEdit,
    onDelete: () => softDeleteEntry(id),
    onReset: () => openConfirmResetTextModal(id),
    onRestore: () => restoreEntry(id),
    onHardDelete: () => openConfirmHardDeleteModal(id),
    onHistory: () => openHistoryModal(id),
  };

  let entryRowClasses = "row entry-row";
  if (deleted) {
    entryRowClasses += " soft-deleted-row";
  }

  return (
    <div ref={setNodeRef} style={style}>
      <div className={entryRowClasses}>
        <div className="col-1 d-flex align-items-center">
          <div className="d-flex justify-content-center">
            {!deleted && (
              <span {...attributes} {...listeners}>
                <FontAwesomeIcon icon={faBars} className="fa-2xl my-0" />
              </span>
            )}
          </div>
        </div>
        <div
          className={`col-${
            editFlag ? 11 : 9
          } text-justify no-margin-paragraph`}
        >
          {editFlag ? (
            <RemirrorEditor
              value={html}
              onChange={(e) => setHtml(e.target.value)}
              toolbarProps={toolbarPluginProps}
            />
          ) : (
            <SafeHtml html={html} />
          )}
        </div>
        {!editFlag && (
          <div className="col-2 d-flex align-items-center">
            <div>
              <RemirrorSectionRowToolbarPlugin {...toolbarPluginProps} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
